import { call, put, select } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'

import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import api from 'src/services/api'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'

import { ResponseDataConferences } from './types'
import * as ConferencesActions from './actions'

import * as RequestsActions from '../requests/actions'

export function* getConferences({
  payload: { options }
}: ReturnType<typeof ConferencesActions.getConferences>) {
  try {
    const {
      conferences,
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    let statusParams = ''

    if (conferences.filterStatus) {
      statusParams = conferences.filterStatus
        .map((status: string) => `&${status}=True`)
        .join('')
    }

    const response: ApiResponse<ResponseDataConferences> = yield call(
      api.get,
      `users/me/conferences/requests?page_size=10&page=${
        options.page
      }${statusParams}${
        options.request_ids && !!options.request_ids.length
          ? `&request_ids=${options.request_ids.join(',')}&organization_id=${
              organization.organization_id
            }`
          : ''
      }`
    )

    if (response.ok) {
      yield put(ConferencesActions.getConferencesSuccess(response.data))
      yield put(
        RequestsActions.setRequestTotals({
          request_totals: response.data.request_totals
        })
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(ConferencesActions.getConferencesFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga getConferences', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(ConferencesActions.getConferencesFailure(message))
  }
}

export function* setConferenceStatus({
  payload: {
    checked_receipts,
    justification,
    request_conference_ids,
    request_id,
    status
  }
}: ReturnType<typeof ConferencesActions.setConferenceStatus>) {
  try {
    const {
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    const response: ApiResponse<ResponseDataConferences> = yield call(
      api.post,
      `/users/me/conferences/requests`,
      {
        request_conferences: request_conference_ids.map(
          request_conference_id => ({
            checked_receipts,
            justification,
            organization_id: organization.organization_id,
            request_conference_id: +request_conference_id,
            request_id: +request_id,
            status
          })
        )
      }
    )

    if (response.ok) {
      const requestNumber = response.data?.requests?.[0]?.request_number || ''
      const message = i18n.t(
        'common.push-notifications.request-changed-successful',
        { value: requestNumber }
      )
      yield put(ConferencesActions.setConferenceStatusSuccess(message))
      return
    }

    if (response.status === 403) {
      //@ts-ignore
      const { exception, reasons } = response?.data || {}
      if (
        exception === 'PolicyViolated' &&
        reasons === 'NeedToCheckAllReceipts'
      ) {
        const message = i18n.t('common.ticket.need-to-check-all-receipts')
        yield put(ConferencesActions.setConferenceStatusFailure(message))
        return
      }
    }

    const message = findBasicApiResponseErrors(response)
    yield put(ConferencesActions.setConferenceStatusFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga setConferenceStatus', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(ConferencesActions.setConferenceStatusFailure(message))
  }
}
